import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import { HiLockClosed, HiCheckCircle } from "react-icons/hi";
import notify from "../components/common/notify";
import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";

/**
 * ResetPassword Page
 * 
 * Handles password reset when user clicks the link from email.
 * URL format: /reset-password?token=<reset_token>
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to reset password");
      }

      setIsSuccess(true);
      notify("Password reset successfully!", "success");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided
  if (!token && !isSuccess) {
    return (
      <>
        <MetaTags title="Reset Password - FunDeSci" />
        <div className="min-h-screen bg-gray-50 py-16">
          <Container>
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="inline-flex w-16 h-16 rounded-2xl bg-red-100 items-center justify-center mb-4">
                <HiLockClosed className="text-red-500" size={28} />
              </div>
              <h1 className="text-2xl font-bold text-secondary mb-2">Invalid Reset Link</h1>
              <p className="text-gray-500 mb-6">
                This password reset link is invalid or has expired.
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all"
              >
                Go to Homepage
              </button>
            </div>
          </Container>
        </div>
      </>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <>
        <MetaTags title="Password Reset - FunDeSci" />
        <div className="min-h-screen bg-gray-50 py-16">
          <Container>
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="inline-flex w-16 h-16 rounded-2xl bg-green-100 items-center justify-center mb-4">
                <HiCheckCircle className="text-green-500" size={28} />
              </div>
              <h1 className="text-2xl font-bold text-secondary mb-2">Password Reset!</h1>
              <p className="text-gray-500 mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all"
              >
                Go to Login
              </button>
            </div>
          </Container>
        </div>
      </>
    );
  }

  // Reset form
  return (
    <>
      <MetaTags title="Reset Password - FunDeSci" />
      <div className="min-h-screen bg-gray-50 py-16">
        <Container>
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primary-green/10 items-center justify-center mb-4">
                <HiLockClosed className="text-primary" size={28} />
              </div>
              <h1 className="text-2xl font-bold text-secondary mb-2">Reset Your Password</h1>
              <p className="text-gray-500">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300 disabled:opacity-70"
              >
                {isLoading ? (
                  <Bars color="#fff" height={20} width={20} wrapperClass="justify-center" />
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/")}
                className="text-primary hover:underline text-sm"
              >
                Back to Login
              </button>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;
